import React, { useState } from "react";
import axios from "axios";
import { ArrowRight, Mail, CheckCircle2, AlertCircle } from "lucide-react";
import PageHero from "../components/PageHero";
import { useLanguage } from "../i18n/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Contato = () => {
  const { t } = useLanguage();
  const c = t.contact;
  const L = c.labels;

  const [form, setForm] = useState({
    nome: "",
    email: "",
    empresa: "",
    telefone: "",
    setor: "",
    mensagem: "",
  });
  const [status, setStatus] = useState({ state: "idle", message: "" });

  const sectors = t.home.sectors.map((s) => s.title);

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.nome || !form.email || !form.mensagem) return;
    setStatus({ state: "loading", message: "" });
    try {
      await axios.post(`${API}/contact`, form);
      setStatus({ state: "success", message: L.success });
      setForm({ nome: "", email: "", empresa: "", telefone: "", setor: "", mensagem: "" });
    } catch (err) {
      setStatus({ state: "error", message: L.error });
    }
  };

  const inputCls =
    "w-full bg-transparent border-0 border-b border-synergia-line focus:border-synergia-green focus:outline-none py-3 font-inter text-base text-synergia-graphite placeholder:text-synergia-graphite/40 transition-colors duration-300";

  return (
    <div data-testid="contact-page">
      <PageHero eyebrow={c.eyebrow} title={c.title} intro={c.intro} testid="contact-hero" />

      <section className="border-b border-synergia-line">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-24 md:py-32 grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-16">
          <div className="md:col-span-4">
            <div className="eyebrow">{c.officeLabel}</div>
            <a
              href={`mailto:${c.officeEmail}`}
              className="mt-6 inline-flex items-center gap-3 font-montserrat font-semibold text-synergia-green text-lg link-underline"
              data-testid="contact-email-link"
            >
              <Mail size={18} /> {c.officeEmail}
            </a>
            <div className="thin-rule my-10" />
            <p className="font-inter text-sm leading-relaxed text-synergia-graphite/70 max-w-xs">
              {t.footer.tagline}
            </p>
          </div>

          <form
            className="md:col-span-8 grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-8"
            onSubmit={onSubmit}
            data-testid="contact-form"
            noValidate
          >
            <div>
              <label className="eyebrow block mb-2" htmlFor="nome">{L.name}</label>
              <input
                id="nome"
                name="nome"
                type="text"
                required
                value={form.nome}
                onChange={onChange}
                className={inputCls}
                data-testid="contact-input-name"
              />
            </div>
            <div>
              <label className="eyebrow block mb-2" htmlFor="email">{L.email}</label>
              <input
                id="email"
                name="email"
                type="email"
                required
                value={form.email}
                onChange={onChange}
                className={inputCls}
                data-testid="contact-input-email"
              />
            </div>
            <div>
              <label className="eyebrow block mb-2" htmlFor="empresa">{L.company}</label>
              <input
                id="empresa"
                name="empresa"
                type="text"
                value={form.empresa}
                onChange={onChange}
                className={inputCls}
                data-testid="contact-input-company"
              />
            </div>
            <div>
              <label className="eyebrow block mb-2" htmlFor="telefone">{L.phone}</label>
              <input
                id="telefone"
                name="telefone"
                type="text"
                value={form.telefone}
                onChange={onChange}
                className={inputCls}
                data-testid="contact-input-phone"
              />
            </div>
            <div className="md:col-span-2">
              <label className="eyebrow block mb-2" htmlFor="setor">{L.sector}</label>
              <select
                id="setor"
                name="setor"
                value={form.setor}
                onChange={onChange}
                className={inputCls}
                data-testid="contact-input-sector"
              >
                <option value="">{L.chooseSector}</option>
                {sectors.map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </div>
            <div className="md:col-span-2">
              <label className="eyebrow block mb-2" htmlFor="mensagem">{L.message}</label>
              <textarea
                id="mensagem"
                name="mensagem"
                rows={5}
                required
                value={form.mensagem}
                onChange={onChange}
                className={`${inputCls} resize-none`}
                data-testid="contact-input-message"
              />
            </div>

            <div className="md:col-span-2 flex flex-wrap items-center gap-6 pt-2">
              <button
                type="submit"
                disabled={status.state === "loading"}
                className="btn-primary disabled:opacity-60 disabled:cursor-not-allowed"
                data-testid="contact-submit"
              >
                {status.state === "loading" ? L.sending : L.submit}
                {status.state !== "loading" && <ArrowRight size={16} />}
              </button>

              {status.state === "success" && (
                <div className="flex items-center gap-2 text-synergia-green font-inter text-sm" data-testid="contact-success">
                  <CheckCircle2 size={18} /> {status.message}
                </div>
              )}
              {status.state === "error" && (
                <div className="flex items-center gap-2 text-red-700 font-inter text-sm" data-testid="contact-error">
                  <AlertCircle size={18} /> {status.message}
                </div>
              )}
            </div>
          </form>
        </div>
      </section>
    </div>
  );
};

export default Contato;
